import {
  ComponentType,
  createContext,
  Dispatch,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import { DynamoFile, FileCategory, FolderItem, PopupTextType } from '../types'
import useFiles from '../hooks/useFiles'
import { errorStringify } from '../utils/error'

export type FilesStore = {
  openPopup: boolean
  popupText: PopupTextType
  upload: boolean
  fileCategory: FileCategory
  selectMultipleFiles: boolean
  folderStack: FolderItem[]
} & (
  | { status: 'idle' }
  | { status: 'loading_files' }
  | { status: 'uploading' }
  | { status: 'loading' }
  | { status: 'error'; errorMessage: string }
)

const initialState: FilesStore = {
  fileCategory: 'ALL',
  folderStack: [],
  openPopup: false,
  popupText: {
    header: '',
    content: '',
    onClick: () => {},
  },
  upload: false,
  status: 'idle',
  selectMultipleFiles: false,
}

export type FileStoreAction =
  | {
      type: 'SET_UPLOADING'
    }
  | {
      type: 'SET_OPEN_POPUP'
      payload: boolean
    }
  | {
      type: 'SET_LOADING'
    }
  | {
      type: 'SET_IDLE'
    }
  | {
      type: 'SET_ERROR'
      payload: string
    }
  | {
      type: 'SET_POPUP_TEXT'
      payload: PopupTextType
    }
  | {
      type: 'SET_UPLOAD'
      payload: boolean
    }
  | {
      type: 'SET_FILE_CATEGORY'
      payload: FileCategory
    }
  | {
      type: 'SET_SELECT_MULTIPLE_FILES'
      payload: boolean
    }
  | {
      type: 'SET_FOLDER_STACK'
      payload: FolderItem[]
    }
  | {
      type: 'BATCH'
      payload: Partial<Omit<FilesStore, 'status' | 'errorMessage'>>
    }

const reducer = (state: FilesStore, action: FileStoreAction): FilesStore => {
  switch (action.type) {
    case 'BATCH': {
      return { ...state, ...action.payload }
    }
    case 'SET_FOLDER_STACK': {
      return { ...state, folderStack: action.payload }
    }
    case 'SET_SELECT_MULTIPLE_FILES': {
      return { ...state, selectMultipleFiles: action.payload }
    }
    case 'SET_FILE_CATEGORY': {
      return { ...state, fileCategory: action.payload }
    }
    case 'SET_UPLOADING': {
      return { ...state, status: 'uploading' }
    }
    case 'SET_UPLOAD': {
      return { ...state, upload: action.payload }
    }
    case 'SET_POPUP_TEXT': {
      return {
        ...state,
        popupText: action.payload,
      }
    }
    case 'SET_IDLE': {
      return { ...state, status: 'idle' }
    }
    case 'SET_LOADING': {
      return { ...state, status: 'loading' }
    }
    case 'SET_ERROR': {
      return { ...state, status: 'error', errorMessage: action.payload }
    }
    case 'SET_OPEN_POPUP': {
      return { ...state, openPopup: action.payload }
    }
    default:
      return { ...state }
  }
}

const FilesContext = createContext<{
  state: FilesStore
  dispatch: Dispatch<FileStoreAction>
  files: DynamoFile[]
  refetch: (...args: any[]) => Promise<any>
}>(null as any)

export const withFilesContext = (Component: ComponentType) => {
  const WrappedComponent = () => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const { error, files, isError, isLoading, refetch } = useFiles()
    const errorRef = useRef(error)

    useEffect(() => {
      if (isLoading) {
        dispatch({ type: 'SET_LOADING' })
      } else {
        dispatch({ type: 'SET_IDLE' })
      }
    }, [isLoading])

    useEffect(() => {
      if (isError) {
        dispatch({
          type: 'SET_ERROR',
          payload: errorStringify(errorRef.current),
        })
      }
    }, [isError])

    return (
      <FilesContext.Provider value={{ dispatch, state, files, refetch }}>
        <Component />
      </FilesContext.Provider>
    )
  }

  return WrappedComponent
}

export const useFilesState = () => {
  const context = useContext(FilesContext)
  if (!context) {
    throw new Error(
      'useDataMatchPageState must be used within a DataMatchPageStateContext Provider'
    )
  }
  return context
}
